import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Ruler,
  Target,
  Check,
  AlertCircle,
  Info,
} from 'lucide-react';
import { profileApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';

export function WeightSettingsPage() {
  const [height, setHeight] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: profileApi.get,
  });

  // Populate form when data loads
  useEffect(() => {
    if (profile) {
      setHeight(profile.height?.toString() || '');
      setTargetWeight(
        profile.targetWeight?.toString().replace('.', ',') || ''
      );
    }
  }, [profile]);

  const updateMutation = useMutation({
    mutationFn: profileApi.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['bmi'] });
      queryClient.invalidateQueries({ queryKey: ['weight-stats'] });
      setSaved(true);
      setError(null);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: () => {
      setError('Virhe tallennettaessa asetuksia');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    let heightNum: number | null = null;
    let targetWeightNum: number | null = null;

    // Validate height
    if (height) {
      heightNum = parseInt(height, 10);
      if (isNaN(heightNum) || heightNum < 50 || heightNum > 300) {
        setError('Pituuden tulee olla väliltä 50-300 cm');
        return;
      }
    }

    // Validate target weight
    if (targetWeight) {
      targetWeightNum = parseFloat(targetWeight.replace(',', '.'));
      if (isNaN(targetWeightNum) || targetWeightNum < 20 || targetWeightNum > 500) {
        setError('Tavoitepainon tulee olla väliltä 20-500 kg');
        return;
      }
    }

    setError(null);
    updateMutation.mutate({
      height: heightNum,
      targetWeight: targetWeightNum,
    });
  };

  const handleClearTargetWeight = () => {
    setTargetWeight('');
    updateMutation.mutate({
      targetWeight: null,
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/weight"
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Painonseurannan asetukset
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Height */}
        <div className="card p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Ruler className="h-5 w-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="font-medium text-gray-900 dark:text-white">
                Pituus
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Käytetään painoindeksin (BMI) laskemiseen
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="number"
              inputMode="numeric"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              placeholder="175"
              min={50}
              max={300}
              className="input flex-1"
            />
            <span className="text-gray-500 dark:text-gray-400">cm</span>
          </div>

          <div className="flex items-start gap-2 mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
            <p className="text-sm text-blue-700 dark:text-blue-300">
              Pituustieto tallennetaan profiiliisi ja sitä käytetään BMI:n
              laskemiseen automaattisesti aina kun kirjaat uuden painon.
            </p>
          </div>
        </div>

        {/* Target Weight */}
        <div className="card p-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Target className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h2 className="font-medium text-gray-900 dark:text-white">
                Tavoitepaino
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Valinnainen - näytetään kaaviossa viiteviivana
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <input
              type="text"
              inputMode="decimal"
              value={targetWeight}
              onChange={(e) => setTargetWeight(e.target.value)}
              placeholder="70,0"
              className="input flex-1"
            />
            <span className="text-gray-500 dark:text-gray-400">kg</span>
          </div>

          {profile?.targetWeight && (
            <button
              type="button"
              onClick={handleClearTargetWeight}
              className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 mt-3"
            >
              Poista tavoitepaino
            </button>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300">
            <AlertCircle className="h-5 w-5" />
            {error}
          </div>
        )}

        {/* Success message */}
        {saved && (
          <div className="flex items-center gap-2 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-green-700 dark:text-green-300">
            <Check className="h-5 w-5" />
            Asetukset tallennettu!
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={updateMutation.isPending}
          className="btn btn-primary w-full"
        >
          {updateMutation.isPending ? 'Tallennetaan...' : 'Tallenna asetukset'}
        </button>
      </form>

      {/* BMI Info */}
      <div className="card p-4">
        <h3 className="font-medium text-gray-900 dark:text-white mb-3">
          Painoindeksi (BMI)
        </h3>
        <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
          <p>Painoindeksi lasketaan kaavalla: paino (kg) / pituus (m)²</p>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <span>&lt; 18,5 - Alipaino</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span>18,5-24,9 - Normaali</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span>25-29,9 - Ylipaino</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <span>&ge; 30 - Lihavuus</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
